/**
 * Fila de envios: o que está na fila agora, o que falta enviar de preço/estoque em cada conta,
 * o histórico das rodadas (de → para, erros) e as últimas tarefas e cargas (inclui o estoque e o preço enviados pelo
 * ERP pela API, com os SKUs e lojas desconhecidos).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, brl, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { can } from '@berytus/contracts';

interface QueuedJob { id: string; type: string; state: 'active' | 'waiting' | 'delayed' | 'failed'; accountId: string | null; createdAt: number; runAt: number | null; attempts: number; error: string | null }
interface Account {
  id: string; channel: string; name: string; status: string; mode: 'off' | 'stock' | 'price' | 'price_stock';
  lastSyncAt: string | null; lastSyncStats: { checked?: number; sent?: number; failed?: number } | null;
  pending: { listings: number; price: number; stock: number; held: Record<string, number> } | null; errors: number;
}
interface Run { id: string; accountId: string; mode: string; status: string; startedAt: string; finishedAt: string | null; stats: { sent?: number; failed?: number; pending?: number }; error: string | null; changes: number }
interface Task { id: string; kind: string; status: string; accountId: string | null; stores: string[] | null; stats: Record<string, unknown>; error: string | null; createdAt: string; startedAt: string | null; finishedAt: string | null }
interface MonitorData { queueAvailable: boolean; queue: QueuedJob[]; accounts: Account[]; runs: Run[]; tasks: Task[] }
interface RunDetail { changes: { sku: string; title: string | null; externalId: string; price?: { from: number | null; to: number }; quantity?: { from: number | null; to: number }; error?: string; note?: string }[] }

const MODE: Record<Account['mode'], string> = { off: 'Envio desligado', stock: 'Só estoque', price: 'Só preço', price_stock: 'Preço e estoque' };
const CHANNEL: Record<string, string> = { mercadolivre: 'Mercado Livre', shopee: 'Shopee' };
const JOB: Record<string, string> = {
  'meli.offers.push': 'Envio de preço/estoque', 'shopee.offers.push': 'Envio de preço/estoque',
  'meli.pull': 'Leitura dos anúncios', 'shopee.pull': 'Leitura dos anúncios', 'meli.catalog': 'Busca de EANs no catálogo do ML',
  'meli.publish': 'Publicação', 'shopee.publish': 'Publicação', 'meli.own.prepare': 'Preparar anúncios próprios', 'shopee.prepare': 'Preparar produtos',
  'meli.orders.backfill': 'Histórico de pedidos', 'shopee.orders': 'Pedidos', 'meli.listings.delete': 'Exclusão de anúncios', 'content.sync': 'Sincronizar anúncios (fotos/descrição)',
};
const TASK: Record<string, string> = {
  'import.qualidoc': 'Carga do catálogo (conector)', 'api.offers': 'Carga de preço/estoque (API)', 'api.products': 'API · produtos', 'api.inventory': 'Estoque pela API',
  'meli.listings.pull': 'Leitura dos anúncios (ML)', 'shopee.listings.pull': 'Leitura dos anúncios (Shopee)', 'meli.catalog.lookup': 'Busca de EANs no catálogo do ML',
  'content.sync': 'Sincronizar anúncios (fotos/descrição)', 'meli.orders.backfill': 'Histórico de pedidos (ML)',
};
const STATE: Record<string, [string, string]> = {
  active: ['Rodando', 'info'], waiting: ['Aguardando', 'off'], delayed: ['Agendada', 'off'], failed: ['Falhou', 'err'],
  running: ['Rodando', 'info'], queued: ['Na fila', 'off'], done: ['Concluída', 'ok'], succeeded: ['Concluída', 'ok'], partial: ['Com erros', 'warn'], cancelled: ['Cancelada', 'off'],
};
const HELD: Record<string, string> = { price_guard: 'retidos pela trava', default_price: 'com preço padrão (não vão)', no_price: 'sem preço', fulfillment_stock: 'no Full (estoque é do ML)', below_min: 'abaixo do mínimo da categoria' };

const st = (s: string) => { const [l, t] = STATE[s] ?? [s, 'off']; return <Tag tone={t}>{l}</Tag>; };
const ago = (ms: number) => {
  const s = Math.round((Date.now() - ms) / 1000);
  return s < 60 ? `há ${s} s` : s < 3600 ? `há ${Math.round(s / 60)} min` : `há ${Math.round(s / 3600)} h`;
};
const duration = (a: string | null, b: string | null) => {
  if (!a) return '';
  const s = ((b ? new Date(b).getTime() : Date.now()) - new Date(a).getTime()) / 1000;
  return s < 60 ? `${Math.round(s)} s` : `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
};

export function MonitorPage() {
  const admin = can(useMe().tenant.access, 'queue', 'write');
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['monitor'], queryFn: () => api<MonitorData>('/monitor'), refetchInterval: 15000 });
  const run = useMutation({
    mutationFn: (id: string) => api(`/channels/accounts/${id}/sync/run`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['monitor'] }),
  });
  const [open, setOpen] = useState<string | null>(null);

  if (q.isPending) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;
  const accName = (id: string | null) => {
    const a = d.accounts.find((x) => x.id === id);
    return a ? `${CHANNEL[a.channel] ?? a.channel} · ${a.name}` : '';
  };
  const active = d.queue.filter((j) => j.state !== 'failed');
  const failed = d.queue.filter((j) => j.state === 'failed');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Fila de envios</h1>
          <p>O que está indo para os canais agora, o que falta enviar e o histórico de cada rodada. Atualiza a cada 15 segundos.</p>
        </div>
      </div>

      <div className="stack-y">
        <section className="panel stack-y" style={{ gap: 12 }}>
          <h2 style={{ margin: 0 }}>Preço e estoque por conta</h2>
          <div style={{ overflowX: 'auto' }}>
            <table className="grid" style={{ width: '100%' }}>
              <thead><tr><th>Conta</th><th>Envio</th><th className="num">Falta enviar</th><th>Retidos</th><th>Último envio</th><th className="num">Com erro</th><th /></tr></thead>
              <tbody>
                {d.accounts.map((a) => {
                  const inQueue = active.find((j) => j.accountId === a.id && j.type.endsWith('offers.push'));
                  return (
                    <tr key={a.id}>
                      <td><b>{CHANNEL[a.channel] ?? a.channel}</b> · {a.name}{a.status !== 'active' && <> <Tag tone="err">{a.status}</Tag></>}</td>
                      <td>{a.mode === 'off' ? <Tag tone="warn">{MODE.off}</Tag> : MODE[a.mode]}{inQueue && <> · {st(inQueue.state)}</>}</td>
                      <td className="num">
                        {a.pending ? <><b>{int(a.pending.listings)}</b><div className="small muted">{a.mode !== 'stock' && `${int(a.pending.price)} preço`}{a.mode === 'price_stock' && ' · '}{a.mode !== 'price' && `${int(a.pending.stock)} estoque`}</div></> : '—'}
                      </td>
                      <td className="small">
                        {a.pending && Object.entries(a.pending.held).filter(([, n]) => n > 0).map(([k, n]) => <div key={k}>{int(n)} {HELD[k] ?? k}</div>)}
                      </td>
                      <td className="small">
                        {a.lastSyncAt ? <>{dateTime(a.lastSyncAt)}<div className="muted">{int(a.lastSyncStats?.sent ?? 0)} enviados · {int(a.lastSyncStats?.failed ?? 0)} erros</div></> : <span className="muted">nunca</span>}
                      </td>
                      <td className="num">{a.errors ? <b>{int(a.errors)}</b> : 0}</td>
                      <td>{admin && a.mode !== 'off' && <button className="btn sm" type="button" disabled={run.isPending} onClick={() => run.mutate(a.id)}>Enviar agora</button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="small muted" style={{ margin: 0 }}>
            "Falta enviar" é a diferença entre a tabela da Berytus e o anúncio agora. A rodada automática roda a cada 15 minutos e logo depois de cada carga de preço ou estoque.
            Retidos pela trava não vão sozinhos: confira no anúncio ou aumente a trava em Configurações do canal → Envio ao canal.
          </p>
          {run.error && <ErrorBox error={run.error} />}
        </section>

        <section className="panel stack-y" style={{ gap: 10 }}>
          <h2 style={{ margin: 0 }}>Na fila agora</h2>
          {!d.queueAvailable ? <span className="small muted">Fila do worker indisponível (Redis).</span>
            : active.length === 0 ? <span className="small muted">Nada na fila.</span> : (
              <div style={{ overflowX: 'auto' }}>
                <table className="grid" style={{ width: '100%' }}>
                  <thead><tr><th>Tarefa</th><th>Conta</th><th>Situação</th><th>Entrou</th></tr></thead>
                  <tbody>
                    {active.map((j) => (
                      <tr key={j.id}>
                        <td>{JOB[j.type] ?? j.type}</td>
                        <td className="small">{accName(j.accountId)}</td>
                        <td>{st(j.state)}{j.state === 'delayed' && j.runAt && <span className="small muted"> · começa {j.runAt > Date.now() ? `em ${Math.max(1, Math.round((j.runAt - Date.now()) / 1000))} s` : 'já'}</span>}</td>
                        <td className="small muted">{ago(j.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          {failed.length > 0 && (
            <details>
              <summary className="small" style={{ cursor: 'pointer' }}>{failed.length} tarefa(s) falharam nas últimas 24 h</summary>
              <ul className="small" style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {failed.map((j) => <li key={j.id}><b>{JOB[j.type] ?? j.type}</b> {accName(j.accountId)} · {ago(j.createdAt)}: {j.error}</li>)}
              </ul>
            </details>
          )}
        </section>

        <section className="panel stack-y" style={{ gap: 10 }}>
          <h2 style={{ margin: 0 }}>Rodadas de envio de preço e estoque</h2>
          {d.runs.length === 0 ? <span className="small muted">Nenhuma rodada com alterações ainda.</span> : (
            <div style={{ overflowX: 'auto' }}>
              <table className="grid" style={{ width: '100%' }}>
                <thead><tr><th>Início</th><th>Conta</th><th>Situação</th><th className="num">Enviados</th><th className="num">Erros</th><th>Duração</th><th /></tr></thead>
                <tbody>
                  {d.runs.map((r) => (
                    <RunRow key={r.id} r={r} account={accName(r.accountId)} open={open === r.id} toggle={() => setOpen(open === r.id ? null : r.id)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="panel stack-y" style={{ gap: 10 }}>
          <h2 style={{ margin: 0 }}>Cargas e tarefas</h2>
          <div style={{ overflowX: 'auto' }}>
            <table className="grid" style={{ width: '100%' }}>
              <thead><tr><th>Quando</th><th>Tarefa</th><th>Conta</th><th>Situação</th><th>Duração</th><th>Detalhe</th></tr></thead>
              <tbody>
                {d.tasks.map((t) => <TaskRow key={t.id} t={t} account={t.stores ? `loja${t.stores.length > 1 ? 's' : ''}: ${t.stores.join(', ') || '—'}` : accName(t.accountId)} />)}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
}

/** Linha de carga/tarefa; as que têm avisos (SKUs ou lojas desconhecidos) abrem a lista. */
function TaskRow({ t, account }: { t: Task; account: string }) {
  const [open, setOpen] = useState(false);
  const warn = Number(t.stats?.unknown ?? 0) + Number(t.stats?.unknownStores ?? 0);
  const ev = useQuery({ queryKey: ['sync-events', t.id, t.finishedAt], queryFn: () => api<{ id: string; level: string; entityRef: string | null; message: string }[]>(`/sync/jobs/${t.id}/events`), enabled: open });
  return (
    <>
      <tr>
        <td className="small">{dateTime(t.createdAt)}</td>
        <td>{TASK[t.kind] ?? t.kind}</td>
        <td className="small">{account}</td>
        <td>{st(t.status)}</td>
        <td className="small muted">{duration(t.startedAt, t.finishedAt)}</td>
        <td className="small" style={{ whiteSpace: 'normal' }}>
          {t.error ?? summary(t.stats)}
          {warn > 0 && <div><button type="button" className="linkish" onClick={() => setOpen(!open)}>{open ? 'esconder' : 'ver quais'}</button></div>}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--surface-2)', whiteSpace: 'normal' }}>
            {ev.error ? <ErrorBox error={ev.error} /> : !ev.data ? <Loading /> : (
              <div className="small" style={{ display: 'grid', gap: 2, maxHeight: 240, overflow: 'auto' }}>
                {ev.data.map((e) => <div key={e.id}><b className="code">{e.entityRef}</b> · {e.message}</div>)}
                {ev.data.length >= 500 && <div className="muted">Mostrando os primeiros 500.</div>}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** Resumo curto dos números da tarefa (só os campos numéricos de primeiro nível). */
const STAT: Record<string, string> = {
  calls: 'chamadas', received: 'recebidos', updated: 'atualizados', unknown: 'SKUs não cadastrados', unknownStores: 'lojas desconhecidas', total: 'total', done: 'feitos',
  found: 'encontrados', notFound: 'não encontrados', items: 'itens', listings: 'anúncios', sent: 'enviados', failed: 'com erro', unchanged: 'sem mudança',
};
function summary(s: Record<string, unknown>) {
  // Na ordem da lista acima (o banco não guarda a ordem dos campos).
  const order = (k: string) => { const i = Object.keys(STAT).indexOf(k); return i < 0 ? 99 : i; };
  return Object.entries(s ?? {}).filter(([, v]) => typeof v === 'number').sort(([a], [b]) => order(a) - order(b)).slice(0, 5)
    .map(([k, v]) => `${int(v as number)} ${v === 1 && k === 'unknownStores' ? 'loja desconhecida' : v === 1 && k === 'unknown' ? 'SKU não cadastrado' : STAT[k] ?? k}`).join(' · ');
}

function RunRow({ r, account, open, toggle }: { r: Run; account: string; open: boolean; toggle: () => void }) {
  const det = useQuery({ queryKey: ['monitor-run', r.id, r.status], queryFn: () => api<RunDetail>(`/monitor/runs/${r.id}`), enabled: open });
  return (
    <>
      <tr>
        <td className="small">{dateTime(r.startedAt)}</td>
        <td className="small">{account}</td>
        <td>{st(r.status)}{r.status === 'running' && r.stats.pending != null && <span className="small muted"> · faltam {int(r.stats.pending)}</span>}</td>
        <td className="num">{int(r.stats.sent ?? 0)}</td>
        <td className="num">{r.stats.failed ? <b>{int(r.stats.failed)}</b> : 0}</td>
        <td className="small muted">{duration(r.startedAt, r.finishedAt)}</td>
        <td>{r.changes > 0 && <button className="btn sm quiet" type="button" onClick={toggle}>{open ? 'Fechar' : 'Ver itens'}</button>}</td>
      </tr>
      {r.error && <tr><td colSpan={7} className="small" style={{ color: 'var(--err)' }}>{r.error}</td></tr>}
      {open && (
        <tr>
          <td colSpan={7}>
            {det.isPending ? <Loading /> : det.error ? <ErrorBox error={det.error} /> : (
              <table className="grid" style={{ width: '100%' }}>
                <thead><tr><th>SKU</th><th>Anúncio</th><th>Produto</th><th className="num">Preço</th><th className="num">Estoque</th><th>Observação</th></tr></thead>
                <tbody>
                  {det.data.changes.map((c, i) => (
                    <tr key={i}>
                      <td className="code">{c.sku}</td>
                      <td className="code">{c.externalId}</td>
                      <td className="small">{c.title}</td>
                      <td className="num">{c.price ? <>{c.price.from == null ? '—' : brl(c.price.from)} → <b>{brl(c.price.to)}</b></> : '—'}</td>
                      <td className="num">{c.quantity ? <>{c.quantity.from ?? '—'} → <b>{c.quantity.to}</b></> : '—'}</td>
                      <td className="small" style={{ color: c.error ? 'var(--err)' : c.note ? 'var(--warn)' : undefined }}>{c.error ?? c.note ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
